// Enums that match backend
import { CustomMap } from './ICustomMap';
import { TActivity } from './Activity';

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  BRAND_AMBASSADOR = 'BRAND_AMBASSADOR',
  CHANNEL_ADMIN = 'CHANNEL_ADMIN',
  CHANNEL_SALES_REP = 'CHANNEL_SALES_REP',
  API_ADMIN = 'API_ADMIN',
  END_CUSTOMER = 'END_CUSTOMER',
}

export enum AccountType {
  BRAND_AMBASSADOR = 'BRAND_AMBASSADOR',
  CHANNEL_PARTNER = 'CHANNEL_PARTNER',
  INTERNAL = 'INTERNAL',
  API_PARTNER = 'API_PARTNER',
  END_CUSTOMER = 'END_CUSTOMER',
}

export enum AccountStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  ARCHIVED = 'ARCHIVED'
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  DEACTIVATED = 'DEACTIVATED',
  SUSPENDED = 'SUSPENDED',
}

// Address type enum
export enum AddressType {
  SHIPPING = 'SHIPPING',
  BILLING = 'BILLING',
  BOTH = 'BOTH'
}

// API Response interface
export interface ApiResponse<T = CustomMap> {
  success: boolean;
  message: string;
  data?: T;
  errors?: CustomMap[];
}

// User interfaces
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  status?: UserStatus;
  accountId?: string;
  accountName?: string;
  accountType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  password?: string;
  role: UserRole;
  accountId?: string;
}

export interface UpdateUserDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: UserRole;
  accountId?: string;
}

/** Dealer tier info (same shape as GET /admin/dealer-discounts/accounts/:accountId/tier) */
export interface DealerTierInfo {
  id: string;
  dealerAccountId: string;
  tierLevel: 'SILVER' | 'GOLD' | 'PLATINUM' | 'SWIFTLANE';
  effectiveStartDate: string;
  createdAt: string;
  isActive: boolean;
  quarterStartDate: string;
  quarterEndDate: string;
  expiryDate: string | null;
}

// Account interfaces
export interface Account {
  id: string;
  accountType: AccountType;
  accountName: string;
  status: AccountStatus;
  netTermsDays: number;
  isTaxExempt?: boolean;
  /**
   * Partner's default selling currency (design 2026-08-05 §A/D1). Set at
   * onboarding (defaults to USD), editable from Command Center via
   * PATCH /accounts/:id/currency (Task 2.11). Existing quotes/orders keep
   * their own currency snapshot — changing this never touches them.
   */
  currency: string;
  accountExecutiveId?: string;
  accountExecutiveName?: string;
  accountExecutiveEmail?: string;
  accountExecutivePhone?: string;
  createdAt: string;
  updatedAt: string;
  commissionTierId?: string; // for Brand Ambassador
  commissionTier?: CommissionTier; // for Brand Ambassadors
  dealerTier?: DealerTierInfo | null; // for Channel Partner / API Partner / Internal (from list API)
  quickbooksCustomerId?: string;
  quickbooksDisplayName?: string;
  onboardingStatus?: string;
  swiftlaneCompanyId?: string;
  swiftlaneWorkspaceName?: string;
  onboardedAt?: string;
  /**
   * Per-partner Apple/Google Wallet status webhook URL. Used only when
   * `accountType === API_PARTNER`. `null` means no per-partner override
   * (the global env-var fallback is used on outbound calls).
   */
  walletWebhookUrl?: string | null;
  /**
   * Masked variant of the webhook secret (e.g. `swk_********…ab12`).
   * The full plain-text value is ONLY ever returned by the rotate API
   * response — never by GET endpoints. `null` means no secret has been
   * generated yet (no `X-Swiftlane-Webhook-Secret` header will be sent).
   */
  walletWebhookSecretMasked?: string | null;
  /** ISO timestamp of the most recent secret rotation; null if never rotated. */
  walletWebhookSecretLastRotatedAt?: string | null;
}

export interface CreateAccountDto {
  accountType: AccountType;
  accountName: string;
  status?: AccountStatus;
  netTermsDays?: number;
  isTaxExempt?: boolean;
  accountExecutiveId?: string;
  adminUser: {
    email: string;
    firstName: string;
    lastName: string;
    phone?: string;
  };
}

export interface UpdateAccountDto {
  accountName?: string;
  status?: AccountStatus;
  netTermsDays?: number;
  isTaxExempt?: boolean;
  accountExecutiveId?: string;
  commissionTierId?: string; // for Brand Ambassador
  /**
   * Per-partner wallet webhook URL. Pass empty string to clear (falls
   * back to env-var URL on outbound calls). HTTPS only — backend
   * validates with an SSRF-safe filter.
   */
  walletWebhookUrl?: string | null;
}

/**
 * Response from `POST /api/admin/accounts/:id/wallet-webhook-secret/rotate`
 * (super admin) and `POST /api/accounts/me/wallet-webhook-secret/rotate`
 * (API_ADMIN self-service). The plain-text `walletWebhookSecret` is
 * returned exactly once — show it to the user with a "save this now"
 * warning, then drop it from state.
 */
export interface RotateWalletWebhookSecretResponse {
  accountId: string;
  walletWebhookSecret: string;            // plain text, shown once
  walletWebhookSecretMasked: string;       // safe for later display
  walletWebhookSecretLastRotatedAt: string; // ISO timestamp
}

// Authentication interfaces
export interface LoginDto {
  email: string;
  password: string;
}

export interface EmailCodeDto {
  email: string;
  code: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

// JWT decoded payload
export interface JwtPayload {
  userId: string;
  email: string;
  role: UserRole;
  accountId?: string;
  exp: number;
}

// Lead status enum
export enum LeadStatus {
  DRAFT = 'DRAFT',
  PENDING_REVIEW = 'PENDING_REVIEW',
  IN_PROGRESS = 'IN_PROGRESS',
  CONVERTED = 'CONVERTED',
  OPEN = 'OPEN'
}

// Opportunity stage enum - matches pipeline: Identify/Prospect → Closed
export enum OpportunityStage {
  IDENTIFY_PROSPECT = 'IDENTIFY_PROSPECT',
  QUALIFICATION = 'QUALIFICATION',
  DEMO_COMPLETED = 'DEMO_COMPLETED',
  SWIFTLANE_QUOTE = 'SWIFTLANE_QUOTE',
  SITE_WALK_THRU = 'SITE_WALK_THRU',
  SWIFTLANE_INSTALL_QUOTE = 'SWIFTLANE_INSTALL_QUOTE',
  NEGOTIATION = 'NEGOTIATION',
  VERBAL_AGREEMENT = 'VERBAL_AGREEMENT',
  BLOOD_COMMITMENT = 'BLOOD_COMMITMENT',
  CLOSED = 'CLOSED',
}

// Opportunity status enum
export enum OpportunityStatus {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED'
}

// Lead interface
export interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  company: string;
  status: LeadStatus;
  source?: string;
  notes?: string;
  jobTitle?: string;
  createdById: string;
  createdBy?: User;
  assignedExecutiveId?: string;
  assignedExecutive?: User;
  partnerAccountId?: string;
  partnerAccount?: Account;
  contactId?: string;
  contact?: Contact;
  customerAccountId?: string;
  customerAccount?: CustomerAccount;
  opportunityId?: string;
  opportunity?: Opportunity;
  salesforceUrl?: string;
  createdAt: string;
  title?: string;
  updatedAt: string;
}

// Contact interface
export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  partnerAccountId?: string;
  partnerAccount?: Account;
  customerAccountId?: string;
  customerAccount?: CustomerAccount;
  createdById: string;
  createdBy?: User;
  createdAt: string;
  updatedAt: string;
  opportunities?: Opportunity[];
  leads?: Lead;
  activities?: TActivity[];
  salesforceUrl?: string;
}

// Customer account interface
export interface CustomerAccount {
  id: string;
  name: string;
  website?: string;
  industry?: string;
  size?: string;
  partnerAccountId?: string;
  partnerAccount?: Account;
  createdById: string;
  createdBy?: User;
  createdAt: string;
  updatedAt: string;
  contacts?: Contact[];
  opportunities?: Opportunity[];
  leads?: Lead[];
  activities?: TActivity[];
  salesforceUrl?: string;
}

// Opportunity interface
export interface Opportunity {
  id: string;
  name: string;
  value: number;
  stage: OpportunityStage;
  status: OpportunityStatus;
  closeDate?: string;
  description?: string;
  bmx_replacement?: boolean;
  demo_required?: boolean;
  type?: string;
  leadSource?: string;
  probability?: number;
  forecastCategoryName?: string;
  nextStep?: string;
  dealType?: string;
  industry?: string;
  market?: string;
  numberOfUnits?: number;
  totalDoors?: number;
  lostReason?: string;
  notes?: string;
  hardwareAmount?: number;
  subscriptionAmount?: number;
  installationAmount?: number;
  partnerAccountId?: string;
  partnerAccount?: Account;
  contacts: Contact[];
  customerAccountId: string;
  customerAccount?: CustomerAccount;
  createdById: string;
  createdBy?: User;
  ownerId?: string;
  owner?: User;
  leadId?: string;
  lead?: Lead;
  salesforceUrl?: string;
  createdAt: string;
  updatedAt: string;
  activities?: TActivity[];
}

// Lead audit log interface
export interface LeadAuditLog {
  id: string;
  leadId: string;
  lead?: Lead;
  action: string;
  performedById: string;
  performedBy?: User;
  note?: string;
  timestamp: string;
}

// DTO interfaces for creating and updating entities
export interface CreateLeadDto {
  fullName: string;
  email: string;
  phone?: string;
  company: string;
  status?: LeadStatus;
  source?: string;
  notes?: string;
  jobTitle?: string;
  assignedExecutiveId?: string;
}

export interface UpdateLeadDto {
  fullName?: string;
  email?: string;
  phone?: string;
  company?: string;
  status?: LeadStatus;
  source?: string;
  notes?: string;
  jobTitle?: string;
  assignedExecutiveId?: string;
}

export interface CreateContactDto {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  title?: string;
  customerAccountId?: string;
}

export interface UpdateContactDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  title?: string;
  customerAccountId?: string;
}

export interface CreateCustomerAccountDto {
  name: string;
  website?: string;
  industry?: string;
  size?: string;
}

export interface UpdateCustomerAccountDto {
  name?: string;
  website?: string;
  industry?: string;
  size?: string;
}

export interface CreateOpportunityDto {
  name: string;
  value: number;
  stage?: OpportunityStage;
  status?: OpportunityStatus;
  closeDate?: string;
  description?: string;
  contactId: string;
  customerAccountId: string;
  leadId?: string;
}

export interface UpdateOpportunityDto {
  name?: string;
  value?: number;
  stage?: OpportunityStage;
  status?: OpportunityStatus;
  closeDate?: string;
  description?: string;
  contactId?: string;
  customerAccountId?: string;
}

export interface ConvertLeadDto {
  contactOption: 'existing' | 'new';
  contactId?: string;
  newContactData?: CreateContactDto;
  accountOption: 'existing' | 'new';
  accountId?: string;
  newAccountData?: CreateCustomerAccountDto;
  opportunityName: string;
  opportunityValue: number;
}

export interface ApiPaginatedResponse<T = CustomMap> extends ApiResponse<T> {
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
}

// Address interfaces
export interface Address {
  id: string;
  partnerAccountId: string;
  fullName: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2?: string | null;
  landmark?: string | null;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
  addressType: AddressType;
  friendlyName?: string | null;
  instructions?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AddressInput {
  fullName: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2?: string;
  landmark?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isDefault?: boolean;
  addressType?: AddressType;
  friendlyName?: string;
  instructions?: string;
}

// Payment Method related interfaces
export interface PaymentMethod {
  id: string;
  type: string;
  brand: string;
  last4: string;
  isDefault: boolean;
  /** ACTIVE | EXPIRED | DECLINED | REMOVED (removed cards only appear on past orders). */
  status?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentMethodsResponse {
  items: PaymentMethod[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

export interface ProductImage {
  id: string;
  productId: string;
  url: string;
  isPrimary: boolean;
  createdAt: string;
}

// Commission Tier interfaces
export interface CommissionTier {
  id: string;
  name: string;
  firstYearPercent: number;
  hasRecurring: boolean;
  recurringPercent?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCommissionTierDto {
  name: string;
  firstYearPercent: number;
  hasRecurring: boolean;
  recurringPercent?: number;
}

export interface UpdateCommissionTierDto {
  name?: string;
  firstYearPercent?: number;
  hasRecurring?: boolean;
  recurringPercent?: number;
}

export type {
  InstallerOrgTier,
  InstallerUserRole,
  InstallerOrgCounts,
  InstallerOrganization,
  InstallerOrganizationCreated,
  InstallerOrganizationListItem,
  InstallerOrgUserRow,
  InstallerOrgProjectRow,
  InstallerOrganizationDetail,
  CreateInstallerOrganizationDto,
} from './installer-org';
